'use client';

// Local thesis overlay. Patches a freshly-submitted thesis onto the
// matching trade row in the trades feed without refetching the entire
// trades query.
//
// Why this exists: the backend SDK's trades cache joins `thesis` from
// the indexer at query time. After the on-chain `add_thesis` confirms,
// the indexer needs a beat to write the thesis and the SDK needs an
// invalidate or a CDC patch to surface it. The SDK exposes only
// `invalidateTradesCache()` (a tag-wide refetch — heavy) and no
// per-row patcher. Rather than refetch every trades query in the app,
// we keep a tiny in-memory map of signature → content and overlay it
// on the SDK response inside the trades feed container.
//
// Lifecycle: the content overlay is per-session only — on reload the
// indexer-joined thesis takes over. Prompt DISMISSALS, in contrast, are
// persisted to localStorage: a dismissed "Add thesis" bar must not come
// back on refresh (MOR-844).

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

/** Keep only the most recent dismissals so the persisted entry stays tiny. */
const MAX_DISMISSED_PROMPTS = 200;

interface LocalThesesState {
  /** Map of trade signature → thesis content. Submitted theses live
   *  here until the page is reloaded, at which point the backend join
   *  takes over. Never persisted. */
  byTradeSignature: ReadonlyMap<string, string>;
  /** Trade signature → dismissal timestamp (ms) for the floating "Add
   *  thesis" prompt the user swiped away / closed (MOR-844). Persisted:
   *  a dismissed prompt stays hidden for THAT trade across reloads; a
   *  new trade mints a new signature and prompts again. */
  dismissedPromptSignatures: Readonly<Record<string, number>>;
  addLocalThesis: (tradeSignature: string, content: string) => void;
  dismissThesisPrompt: (tradeSignature: string) => void;
}

function getUsableLocalStorage(): Storage | null {
  const storage = globalThis.localStorage;
  if (
    storage === undefined ||
    typeof storage.getItem !== 'function' ||
    typeof storage.setItem !== 'function' ||
    typeof storage.removeItem !== 'function'
  ) {
    return null;
  }
  return storage;
}

const SAFE_STORAGE: StateStorage = {
  getItem: (name: string): string | null => getUsableLocalStorage()?.getItem(name) ?? null,
  setItem: (name: string, value: string): void => {
    getUsableLocalStorage()?.setItem(name, value);
  },
  removeItem: (name: string): void => {
    getUsableLocalStorage()?.removeItem(name);
  },
};

/** Oldest-first trim so the record never outgrows `MAX_DISMISSED_PROMPTS`. */
function trimDismissals(entries: Record<string, number>): Record<string, number> {
  const keys = Object.keys(entries);
  if (keys.length <= MAX_DISMISSED_PROMPTS) return entries;
  const sorted = keys.sort((a, b) => (entries[a] ?? 0) - (entries[b] ?? 0));
  const next: Record<string, number> = {};
  for (const key of sorted.slice(keys.length - MAX_DISMISSED_PROMPTS)) {
    const ts = entries[key];
    if (ts !== undefined) next[key] = ts;
  }
  return next;
}

export const useLocalThesesStore: UseBoundStore<StoreApi<LocalThesesState>> =
  createTrackedStore<LocalThesesState>('useLocalThesesStore')(
    persist(
      (set) => ({
        byTradeSignature: new Map<string, string>(),
        dismissedPromptSignatures: {},
        addLocalThesis(tradeSignature: string, content: string): void {
          if (tradeSignature.length === 0 || content.length === 0) return;
          set((state) => {
            const next = new Map(state.byTradeSignature);
            next.set(tradeSignature, content);
            return { byTradeSignature: next };
          });
        },
        dismissThesisPrompt(tradeSignature: string): void {
          if (tradeSignature.length === 0) return;
          set((state) => {
            if (state.dismissedPromptSignatures[tradeSignature] !== undefined) return state;
            return {
              dismissedPromptSignatures: trimDismissals({
                ...state.dismissedPromptSignatures,
                [tradeSignature]: Date.now(),
              }),
            };
          });
        },
      }),
      {
        name: 'morfi-thesis-prompt-dismissals',
        storage: createJSONStorage(() => SAFE_STORAGE),
        // Only the dismissals persist — the content overlay stays in-memory
        // by design (the backend join owns it after a reload).
        partialize: (
          state: LocalThesesState,
        ): Pick<LocalThesesState, 'dismissedPromptSignatures'> => ({
          dismissedPromptSignatures: state.dismissedPromptSignatures,
        }),
      },
    ),
  );
